import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import {
  getAllCombinedRoomsForSurvey,
  getRandomID,
  parseXLSX,
  readURLAsBinary,
  removeDuplicatesFromArray,
} from '../../../../../shared/utilities';
import {
  exportRoomSchedule,
  setFormData,
  useRoomScheduleStepData,
} from '../../../../../state/survey-form';
import {
  AddEditRoomProps,
  RoomProps,
  RoomScheduleRoomProps,
} from '../../../../../types/room';

import ErrorBoundary from '../../../../../components/ErrorBoundary';
import FileUploaderCard from '../../../../../components/FileUploaderCard';
import BackButton from '../../../../../components/Form/BackButton';
import ButtonsContainer from '../../../../../components/Form/ButtonsContainer';
import ErrorText from '../../../../../components/Form/ErrorText';
import NextButton from '../../../../../components/Form/NextButton';
import { useModal } from '../../../../../components/Modal';
import StepGuide from '../../../../../components/StepGuide';
import Table from '../../../../../components/Table';
import TableActionButton from '../../../../../components/TableActionButton';
import { AppState, useAppDispatch } from '../../../../../config/store';
import { EMPTY_ARRAY } from '../../../../../shared/helpers';
import {
  getRoomsOmittedFromScheduleReplacement,
  getRoomScheduleWithImportedRooms,
  getRoomScheduleSetupDeletionConfirmationReason,
  markOmittedRoomsForDeletionInScheduleReplacement,
} from '../../../../../shared/roomScheduleSetup';
import { addFileObject } from '../../../../../state/files';
import { setSnackMessage } from '../../../../../state/ui';
import { StepperStepProps } from '../../../../../types/form';
import { UrlParams } from '../../../../../types/urlParams';
import useLeaveHandler from '../../hooks/useLeaveHandler';
import AddorEditRoomForm from './components/AddorEditRoomForm';
import { generateRoomScheduleSheet } from './util';
import {
  getRoomList,
  getRoomScheduleSaveUpdate,
  isValidRoomScheduleHeaderCount,
  roomScheduleImportColumns,
} from './utils';
import { isLoaded, useFirestoreConnect } from 'react-redux-firebase';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

const columns = [
  { accessorKey: 'floor', header: 'Floor' },
  { accessorKey: 'name', header: 'Room' },
  {
    accessorKey: 'areaFloor',
    header: 'Area (Floor & Ceiling)',
  },
  {
    accessorKey: 'areaWall',
    header: 'Area (Wall)',
  },
  { accessorKey: 'notes', header: 'Notes' },
];

const RoomSchedule = ({ onSubmit, onBack, onLeave }: StepperStepProps) => {
  const { roomSchedule, importData, floorPlans, surveyName } =
    useRoomScheduleStepData();
  const { clientId, surveyId } = useParams<UrlParams>();

  const dispatch = useAppDispatch();
  const setupRoomDocs = useSelector(
    (state: AppState) => state.firestore.ordered.setupRooms,
  );
  const setupRooms = useMemo(
    () =>
      surveyId
        ? (setupRoomDocs || EMPTY_ARRAY).filter(
            (room) => room.surveyId === surveyId,
          )
        : EMPTY_ARRAY,
    [setupRoomDocs, surveyId],
  );
  const setupRoomsLoaded = !surveyId || isLoaded(setupRoomDocs);

  useFirestoreConnect(
    surveyId
      ? [
          {
            collection: 'clients',
            doc: clientId,
            subcollections: [
              { collection: 'surveys', doc: surveyId },
              { collection: 'rooms' },
            ],
            where: ['deleted', '==', 0],
            storeAs: 'setupRooms',
          },
        ]
      : [],
  );

  const { handleSubmit, reset, getValues, control } = useForm();

  const { errors } = useFormState({
    control,
  });

  useLeaveHandler(onLeave, getValues);

  const { showModal } = useModal();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [roomToEdit, setRoomToEdit] = useState<RoomProps>();

  const roomScheduleData = roomSchedule?.data || [];
  const combinedRoomScheduleData = useMemo(() => {
    return getAllCombinedRoomsForSurvey(
      { roomSchedule },
      setupRooms,
      undefined,
      undefined,
      true,
    ) as RoomProps[];
  }, [roomSchedule, setupRooms]);
  const roomScheduleFloors = roomSchedule?.floors || [];

  const getRoomDeletionConfirmationMessage = useCallback(
    (room: Partial<RoomProps>) => {
      const confirmationReason =
        getRoomScheduleSetupDeletionConfirmationReason(room);

      if (confirmationReason === 'imported') {
        return 'This room was created as part of an asset data import. Deleting it will also delete any assets and partial renewals within it.';
      }

      if (confirmationReason === 'assets') {
        return 'This room contains assets. Deleting it will also delete any assets and partial renewals within it.';
      }

      return undefined;
    },
    [],
  );

  const getRoomRemovalBlockedMessage = useCallback(() => {
    if (surveyId && !setupRoomsLoaded) {
      return 'Room asset data is still loading. Please try again in a moment.';
    }

    return undefined;
  }, [setupRoomsLoaded, surveyId]);

  const removeFloorPlanMarkersForRooms = useCallback(
    (roomIds: string[]) => {
      if (!floorPlans?.length || !roomIds.length) {
        return floorPlans;
      }

      const deletedRoomIds = new Set(roomIds);
      return floorPlans.map((fp) => ({
        ...fp,
        markers:
          fp.markers?.filter((marker) => !deletedRoomIds.has(marker.roomId)) ||
          [],
      }));
    },
    [floorPlans],
  );

  const confirmScheduleDeletion = useCallback(
    async (roomsToDelete: Partial<RoomProps>[]) => {
      if (!roomsToDelete.length) {
        return true;
      }

      return showModal({
        title: 'Please Confirm',
        messages: [
          `This will delete ${roomsToDelete.length} room${
            roomsToDelete.length === 1 ? '' : 's'
          } from the survey.`,
          'Any assets and partial renewals within the deleted rooms will also be deleted when the survey is saved.',
        ],
        buttons: {
          confirm: [{ text: 'Delete', value: true }],
          cancel: [{ text: 'Cancel', value: false }],
        },
      });
    },
    [showModal],
  );

  const setFileData = async (file, id) => {
    const { lastModified, name } = file;

    const fileUrl = window.URL.createObjectURL(file);
    dispatch(addFileObject({ fileUrl, fileName: name, id, lastModified }));

    const data = await readURLAsBinary(file);

    const roomListParsed = await parseXLSX<RoomScheduleRoomProps>(
      data,
      roomScheduleImportColumns,
    );

    if (!isValidRoomScheduleHeaderCount(roomListParsed.headers.length)) {
      return showModal({
        title: 'Invalid File',
        messages: [
          'The file you uploaded is not a valid room schedule.',
          '',
          'Please download the room schedule template and try again, ensuring your columns match the template ordering exactly.',
          '',
          'Should you require assistance, please contact support@asseticom.co.uk',
        ],
        buttons: {
          confirm: [{ text: 'Ok', value: true }],
        },
      });
    }

    const { rooms } = getRoomList(
      roomListParsed.rows,
      combinedRoomScheduleData,
    );

    let allRooms = [...rooms];

    const replaceRooms = roomScheduleData.length
      ? await showModal({
          title: 'Please Confirm',
          messages: [
            'This survey already contains a room schedule, would you like to replace it or merge into it?',
            'If you replace it then you may lose any floor plan settings associated with the previous room schedule.',
          ],
          buttons: {
            confirm: [{ text: 'Replace', value: true }],
            cancel: [{ text: 'Merge', value: false }],
          },
        })
      : true;

    if (!replaceRooms) {
      allRooms = [...roomScheduleData, ...allRooms];
    } else {
      const omittedRooms = getRoomsOmittedFromScheduleReplacement({
        existingRooms: combinedRoomScheduleData,
        replacementRooms: allRooms,
      });

      const deletionConfirmed = await confirmScheduleDeletion(omittedRooms);
      if (!deletionConfirmed) {
        return;
      }

      allRooms = markOmittedRoomsForDeletionInScheduleReplacement({
        existingRooms: combinedRoomScheduleData,
        replacementRooms: allRooms,
        deletedAt: Date.now(),
      });
    }

    const deduped = allRooms
      .reverse()
      .filter(
        (room, index) =>
          index ===
          allRooms.findIndex(
            (other) => room.name === other.name && room.floor === other.floor,
          ),
      )
      .reverse();

    const activeRooms = deduped.filter((room) => !room.deleted);
    const allFloors = removeDuplicatesFromArray(
      activeRooms.map((r) => r.floor),
    );
    const omittedRoomIds = deduped
      .filter((room) => room.deleted)
      .map((room) => room.id);

    dispatch(
      setFormData({
        roomSchedule: {
          data: deduped,
          fileName: name,
          lastModified,
          floors: allFloors,
        },
        floorPlans: replaceRooms
          ? removeFloorPlanMarkersForRooms(omittedRoomIds)
          : floorPlans,
      }),
    );
  };

  useEffect(() => {
    const roomScheduleUpdate = getRoomScheduleWithImportedRooms({
      importedRooms: importData?.rooms,
      roomSchedule,
    });

    if (roomScheduleUpdate !== roomSchedule) {
      dispatch(setFormData({ roomSchedule: roomScheduleUpdate }));
    }

    reset({ roomSchedule: roomScheduleUpdate });
  }, [dispatch, importData?.rooms, roomSchedule, reset]);

  const rows = useMemo(
    () =>
      combinedRoomScheduleData.length
        ? combinedRoomScheduleData.map((room) => {
            const { areaFloor, areaWall } = room;
            return {
              ...room,
              areaFloor:
                typeof areaFloor === 'string'
                  ? parseFloat(areaFloor).toFixed(1)
                  : areaFloor,
              areaWall:
                typeof areaWall === 'string'
                  ? parseFloat(areaWall).toFixed(1)
                  : areaWall,
            };
          })
        : null,
    [combinedRoomScheduleData],
  );

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const clearRoomScheduleAndFloorplans = async (skipConfirmation = false) => {
    const roomsToDelete = getRoomsOmittedFromScheduleReplacement({
      existingRooms: combinedRoomScheduleData,
      replacementRooms: [],
    });
    if (!skipConfirmation) {
      const deletionConfirmed = await confirmScheduleDeletion(roomsToDelete);
      if (!deletionConfirmed) {
        return;
      }
    }

    const roomsMarkedForDeletion =
      markOmittedRoomsForDeletionInScheduleReplacement({
        existingRooms: combinedRoomScheduleData,
        replacementRooms: [],
        deletedAt: Date.now(),
      });

    if (roomsMarkedForDeletion.length) {
      dispatch(
        setFormData({
          roomSchedule: { data: roomsMarkedForDeletion, floors: [] },
        }),
      );
    } else {
      dispatch(setFormData({ roomSchedule: undefined }));
    }
    dispatch(setFormData({ floorPlans: undefined }));

    handleDialogClose();
  };

  const onClickedRemove = () => {
    if (floorPlans?.length) {
      return setDialogOpen(true);
    }
    return clearRoomScheduleAndFloorplans();
  };

  const onRoomSaved = (roomData: AddEditRoomProps) => {
    const trimmedName = roomData.name.trim();
    const trimmedFloor = roomData.floor.trim();
    const editingRoom = !!roomToEdit?.id;
    const editingRoomId = roomToEdit?.id;

    const existingRoom = roomScheduleData.find(
      (r) =>
        r.name === trimmedName && r.floor === trimmedFloor && r.deleted === 0,
    );

    if (
      (!editingRoom && existingRoom) ||
      (editingRoom && existingRoom && existingRoom.id !== editingRoomId)
    ) {
      return showModal({
        title: 'Room Exists',
        messages: [
          'That room already exists in the room schedule, please enter a different name or floor.',
        ],
        buttons: {
          confirm: [{ text: 'Ok', value: true }],
        },
      });
    }

    const roomScheduleUpdate = getRoomScheduleSaveUpdate({
      roomData,
      roomScheduleData,
      editingRoomId: editingRoom && editingRoomId ? editingRoomId : undefined,
      getId: getRandomID,
    });
    dispatch(
      setFormData({
        roomSchedule: roomScheduleUpdate,
      }),
    );
    dispatch(
      setSnackMessage({
        message: `${roomData.name} ${editingRoom ? 'updated' : 'added'}`,
      }),
    );
    setRoomToEdit(undefined);
  };

  const showRoomChangeDisallowedModal = useCallback(
    (operation: string, message?: string) => {
      showModal({
        title: `Room cannot be ${operation}`,
        messages: [
          message ||
            `This room was created as part of an asset data import and cannot be ${operation}.`,
        ],
        buttons: { confirm: [{ text: 'Ok', value: true }] },
      });
    },
    [showModal],
  );

  const onClickedRoomDelete = useCallback(
    async (room: RoomProps) => {
      setRoomToEdit(undefined);
      const blockedMessage = getRoomRemovalBlockedMessage();
      if (blockedMessage) {
        return showRoomChangeDisallowedModal('deleted', blockedMessage);
      }
      const deletionConfirmationMessage =
        getRoomDeletionConfirmationMessage(room);

      if (deletionConfirmationMessage) {
        const deletionConfirmed = await showModal({
          title: 'Please Confirm',
          messages: [deletionConfirmationMessage],
          buttons: {
            confirm: [{ text: 'Delete', value: true }],
            cancel: [{ text: 'Cancel', value: false }],
          },
        });

        if (!deletionConfirmed) {
          return;
        }
      }

      const deletedAt = Date.now();
      const roomWasInSchedule = roomScheduleData.some((r) => r.id === room.id);
      const deletedRoom = {
        areaFloor: Number(room.areaFloor || 0),
        areaWall: Number(room.areaWall || 0),
        deleted: deletedAt,
        floor: room.floor,
        id: room.id,
        name: room.name,
        ...(room.imported === undefined ? {} : { imported: room.imported }),
        ...(room.notes === undefined ? {} : { notes: room.notes }),
      };
      const withRoomRemoved = roomWasInSchedule
        ? roomScheduleData.map((r) =>
            r.id === room.id ? { ...r, deleted: deletedAt } : r,
          )
        : [...roomScheduleData, deletedRoom];
      const floors = removeDuplicatesFromArray(
        withRoomRemoved.filter((r) => !r.deleted).map((r) => r.floor),
      );
      const floorPlanForRoom = floorPlans?.find(
        (fp) => fp.floor === room.floor,
      );
      const floorPlanForRoomWithMarkerRemoved =
        floorPlanForRoom?.markers.filter((m) => m.roomId !== room.id);
      const updatedFloorPlans = floorPlans?.map((fp) => {
        if (fp.floor === room.floor) {
          return {
            ...fp,
            markers: floorPlanForRoomWithMarkerRemoved,
          };
        }
        return fp;
      });
      dispatch(
        setFormData({
          roomSchedule: {
            data: withRoomRemoved,
            floors,
          },
          floorPlans: updatedFloorPlans,
        }),
      );
    },
    [
      roomScheduleData,
      floorPlans,
      showRoomChangeDisallowedModal,
      getRoomRemovalBlockedMessage,
      getRoomDeletionConfirmationMessage,
      dispatch,
      showModal,
    ],
  );

  const onClickedRoomEdit = useCallback(
    (room: RoomProps) => {
      if (room.imported) {
        return showRoomChangeDisallowedModal('edited');
      }
      setRoomToEdit(room);
    },
    [showRoomChangeDisallowedModal],
  );

  const actionButtons = useCallback(
    ({ row, closeMenu }) => {
      const buttons: JSX.Element[] = [];

      const rowData = row.original;

      buttons.push(
        <TableActionButton
          key="Delete"
          text="Delete"
          colour="red"
          onClick={() => {
            closeMenu();
            onClickedRoomDelete(rowData);
          }}
        />,
        <TableActionButton
          key="Edit"
          text="Edit"
          colour="blue"
          onClick={() => {
            closeMenu();
            onClickedRoomEdit(rowData);
          }}
        />,
      );

      return (
        <Stack direction="column" p={1}>
          {buttons}
        </Stack>
      );
    },
    [onClickedRoomDelete, onClickedRoomEdit],
  );

  return (
    <Box p={2}>
      <ErrorBoundary>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12}>
              <Stack direction="column" alignItems="center">
                <StepGuide
                  stepNumber={1}
                  text="Download the room schedule template."
                />
                <Button
                  onClick={() => {
                    generateRoomScheduleSheet(
                      columns.map((c) => c.header),
                      surveyName,
                    );
                  }}
                  variant="contained"
                  color="secondary"
                >
                  Download Room Schedule Template
                </Button>
              </Stack>

              <Box my={4}>
                <StepGuide
                  stepNumber={2}
                  text="Upload your completed template file."
                />
                <FileUploaderCard
                  title="Room Schedule"
                  onChange={(file) => setFileData(file, 'roomSchedule')}
                  onRemove={roomSchedule && onClickedRemove}
                  fileName={roomSchedule?.fileName}
                />
                {errors.roomSchedule && (
                  <ErrorText text="You must upload a room schedule" />
                )}
              </Box>

              {rows?.length ? (
                <Box>
                  <StepGuide stepNumber={3} text="Check your data" />
                  <Table
                    rows={rows}
                    columns={columns}
                    disableSearchParams
                    rowsPerPage={10}
                    tableProps={{
                      getRowId: (originalRow) => originalRow.id,
                      enableTopToolbar: false,
                      renderRowActionMenuItems: actionButtons,
                      enableRowActions: true,
                      positionActionsColumn: 'first',
                    }}
                  />
                </Box>
              ) : null}
            </Grid>
          </Grid>

          {rows?.length ? (
            <>
              <AddorEditRoomForm
                room={roomToEdit}
                floors={roomScheduleFloors}
                onRoomSaved={onRoomSaved}
                onCancel={() => setRoomToEdit(undefined)}
              />
              <Stack direction="row" justifyContent="flex-end" my={2}>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    dispatch(exportRoomSchedule());
                  }}
                >
                  Export Room Schedule
                </Button>
              </Stack>
            </>
          ) : null}
          <ButtonsContainer>
            <BackButton onBack={onBack} />
            <NextButton />
          </ButtonsContainer>
        </form>
        <Dialog
          open={dialogOpen}
          onClose={handleDialogClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">Please Confirm</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to remove this room schedule? This will also
              remove any floorplans and their associated markers. Any assets and
              partial renewals within the deleted rooms will also be deleted
              when the survey is saved.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button color="secondary" onClick={handleDialogClose}>
              Cancel
            </Button>
            <Button
              onClick={() => clearRoomScheduleAndFloorplans(true)}
              color="primary"
            >
              Remove
            </Button>
          </DialogActions>
        </Dialog>
      </ErrorBoundary>
    </Box>
  );
};

export default RoomSchedule;
